import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { createOptionValue, deleteOptionValue, updateOptionValue } from '../../api/business/endpoints';
import type { OptionGroup, OptionValue, TextChanges } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LocalisedTextField } from '../../components/LocalisedTextField';
import { missingLanguages, textChanges, textsOf } from '../../components/localisedText';
import { MissingLanguagesChip } from '../../components/MissingLanguagesChip';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useT } from '../../i18n/LanguageContext';

type OptionValuesDialogProps = {
  companyId: string;
  group: OptionGroup;
  canManage: boolean;
  onClose: () => void;
};

/**
 * A group's values in order: add, rename in every content language, move, delete. A value a variant holds is
 * refused deletion by the API, which says why.
 */
export const OptionValuesDialog = ({ companyId, group, canManage, onClose }: OptionValuesDialogProps) => {
  const t = useT();
  const { languages, available } = useContentLanguages();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{ id: string; texts: Record<string, string> } | null>(null);
  const invalidates = ['option-groups', 'product'];
  const defaultLanguage = languages[0];

  const add = useAppMutation(
    (texts: TextChanges) => createOptionValue(companyId, group.id, texts),
    invalidates,
    () => setDraft({}),
  );
  const change = useAppMutation(
    ({ id, ...changes }: { id: string; texts?: TextChanges; position?: number }) =>
      updateOptionValue(companyId, group.id, id, changes),
    invalidates,
    () => setEditing(null),
  );
  const remove = useAppMutation((id: string) => deleteOptionValue(companyId, group.id, id), invalidates);
  const busy = add.isPending || change.isPending || remove.isPending;

  const row = (value: OptionValue, index: number) =>
    editing?.id === value.id ? (
      <ListItem key={value.id} disableGutters>
        <Stack
          component="form"
          direction="row"
          spacing={1}
          alignItems="flex-end"
          sx={{ width: '100%' }}
          onSubmit={(event) => {
            event.preventDefault();
            change.mutate({
              id: value.id,
              texts: textChanges(editing.texts, textsOf(value.translations, 'value', languages), languages),
            });
          }}
        >
          <LocalisedTextField
            size="small"
            autoFocus
            label={t('optionGroups.renameValue')}
            languages={languages}
            available={available}
            values={editing.texts}
            onChange={(texts) => setEditing({ id: value.id, texts })}
            maxLength={80}
          />
          <Button type="submit" disabled={busy || (editing.texts[defaultLanguage] ?? '').trim() === ''}>
            {t('common.save')}
          </Button>
          <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
        </Stack>
      </ListItem>
    ) : (
      <ListItem
        key={value.id}
        disableGutters
        secondaryAction={
          canManage ? (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={t('common.moveUp')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || index === 0}
                    onClick={() => change.mutate({ id: value.id, position: index - 1 })}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common.moveDown')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || index === group.values.length - 1}
                    onClick={() => change.mutate({ id: value.id, position: index + 1 })}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('optionGroups.renameValue')}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setEditing({ id: value.id, texts: textsOf(value.translations, 'value', languages) })
                  }
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.delete')}>
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    disabled={busy}
                    onClick={() => remove.mutate(value.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ) : undefined
        }
      >
        <ListItemText
          primary={
            <>
              {value.value}
              <MissingLanguagesChip
                missing={missingLanguages(textsOf(value.translations, 'value', languages), languages)}
              />
            </>
          }
        />
      </ListItem>
    );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('optionGroups.valuesTitle', { name: group.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <ErrorNotice error={add.error ?? change.error ?? remove.error} />
          {group.values.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('optionGroups.noValues')}
            </Typography>
          ) : (
            <List dense>{group.values.map(row)}</List>
          )}
          {canManage ? (
            <Stack
              component="form"
              direction="row"
              spacing={1}
              alignItems="flex-end"
              onSubmit={(event) => {
                event.preventDefault();
                add.mutate(textChanges(draft, null, languages));
              }}
            >
              <LocalisedTextField
                size="small"
                label={t('optionGroups.newValue')}
                languages={languages}
                available={available}
                values={draft}
                onChange={setDraft}
                maxLength={80}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={busy || (draft[defaultLanguage] ?? '').trim() === ''}
              >
                {t('optionGroups.addValue')}
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
